'use client';

import { useRef, useEffect } from 'react';
import { Phone } from 'lucide-react';
import { countryDialCodes } from '@/lib/data/country-dial-codes';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface PhoneInputProps {
  id?: string;
  name: string;
  value: string;
  countryCode: string;
  country: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCountryChange?: (country: string) => void;
  onFocus?: () => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export function PhoneInput({
  id,
  name,
  value,
  countryCode,
  country,
  onChange,
  onCountryChange,
  onFocus,
  onClick,
  onKeyDown,
  error,
  required = false,
  placeholder,
  className = '',
}: PhoneInputProps) {
  const t = useTranslations('form');
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const codeLength = countryCode ? countryCode.length + 1 : 0; // +1 for the space
  const phoneValue = countryCode ? `${countryCode} ${value}` : value;

  const defaultPlaceholder = placeholder || t('phonePlaceholder') || 'Your phone number';

  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Prevent typing if no country is selected
    if (!countryCode || !country) {
      if (phoneInputRef.current) {
        phoneInputRef.current.value = '';
      }
      setTimeout(() => {
        phoneInputRef.current?.blur();
        const countrySelect = document.getElementById(id?.replace('phone', 'country') || 'country');
        if (countrySelect) {
          countrySelect.focus();
        }
      }, 100);
      return;
    }

    // Remove country code and space from the value, keep only digits
    let cleanedValue = inputValue;
    if (countryCode) {
      cleanedValue = cleanedValue.replace(new RegExp(`^\\${countryCode}\\s*`), '');
    } else {
      cleanedValue = cleanedValue.replace(/^\+?\d+\s*/, '');
    }

    // Keep only digits
    const digitsOnly = cleanedValue.replace(/\D/g, '');

    // Create synthetic event with cleaned value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name,
        value: digitsOnly,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);

    // Ensure cursor position is maintained after country code and space
    if (phoneInputRef.current && countryCode) {
      setTimeout(() => {
        if (phoneInputRef.current) {
          const newCursorPos = Math.max(codeLength, phoneInputRef.current.selectionStart || codeLength);
          phoneInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (!countryCode || !country) {
      setTimeout(() => {
        phoneInputRef.current?.blur();
        const countrySelect = document.getElementById(id?.replace('phone', 'country') || 'country');
        if (countrySelect) {
          countrySelect.focus();
        }
      }, 100);
      return;
    }

    if (phoneInputRef.current && countryCode) {
      setTimeout(() => {
        if (phoneInputRef.current) {
          const cursorPos = codeLength;
          phoneInputRef.current.setSelectionRange(cursorPos, cursorPos);
        }
      }, 0);
    }

    onFocus?.();
  };

  // Handle click
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!countryCode || !country) {
      setTimeout(() => {
        phoneInputRef.current?.blur();
        const countrySelect = document.getElementById(id?.replace('phone', 'country') || 'country');
        if (countrySelect) {
          countrySelect.focus();
        }
      }, 100);
      return;
    }

    if (phoneInputRef.current && countryCode) {
      const input = phoneInputRef.current;
      const cursorPos = input.selectionStart || 0;

      if (cursorPos < codeLength) {
        setTimeout(() => {
          input.setSelectionRange(codeLength, codeLength);
        }, 0);
      }
    }

    onClick?.(e);
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!phoneInputRef.current) return;

    if (!countryCode || !country) {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', 'Escape'].includes(e.key)) {
        return;
      }

      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter') {
        e.preventDefault();
        setTimeout(() => {
          phoneInputRef.current?.blur();
          const countrySelect = document.getElementById(id?.replace('phone', 'country') || 'country');
          if (countrySelect) {
            countrySelect.focus();
          }
        }, 100);
      }
      return;
    }

    const input = phoneInputRef.current;
    const cursorPos = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    if (cursorPos < codeLength) {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
        if (e.key === 'ArrowLeft' && cursorPos === codeLength) {
          e.preventDefault();
          input.setSelectionRange(codeLength, codeLength);
        } else if (e.key === 'Home') {
          e.preventDefault();
          input.setSelectionRange(codeLength, codeLength);
        }
        return;
      }

      if (['Backspace', 'Delete'].includes(e.key)) {
        e.preventDefault();
        input.setSelectionRange(codeLength, codeLength);
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        input.setSelectionRange(codeLength, codeLength);
      }
    }

    if (selectionEnd < codeLength && ['Backspace', 'Delete'].includes(e.key)) {
      e.preventDefault();
      input.setSelectionRange(codeLength, codeLength);
    }

    const spacePosition = countryCode.length;
    if (cursorPos === spacePosition && e.key === 'Backspace') {
      e.preventDefault();
      input.setSelectionRange(codeLength, codeLength);
    }

    if (cursorPos === spacePosition && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End' && e.key !== 'Tab') {
      e.preventDefault();
      input.setSelectionRange(codeLength, codeLength);
    }

    onKeyDown?.(e);
  };

  return (
    <div>
      <div className="group relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
          <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
        </div>
        <input
          ref={phoneInputRef}
          type="tel"
          id={id || name}
          name={name}
          value={phoneValue}
          onChange={handlePhoneChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          className={cn(
            'w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:outline-none transition-all duration-300',
            error
              ? 'border-red-500 dark:border-red-500 focus:border-red-500 dark:focus:border-red-500 focus:shadow-red-500/20'
              : 'border-gray-200/50 dark:border-gray-700/50 focus:border-primary-500 dark:focus:border-primary-400 focus:shadow-primary-500/20',
            className
          )}
          placeholder={countryCode ? `${countryCode} ${defaultPlaceholder}` : defaultPlaceholder}
          required={required}
        />
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

